import React, { ReactNode, useCallback } from 'react';
import { ReactComponentLike } from 'prop-types';

import { Tabs, Tab } from '../../shared/tabs.component';

import ViewGrid from './viewGrid.component';
import AdminEntityImport from './adminEntityImport.component';
import { TabTypes } from './constants';
import { ColumnDefinitions } from '../../shared/types/common';
import { ComparatorConfig, VoidParametrisedFunction } from './types';
import { useDefaultTab } from './useDefaultTab';

type AdminPageTabProps = {
  pageTitle: string,
  activeTab: string,
  canModify?: boolean,
  changeTab: VoidParametrisedFunction,
  adminColumnRenderer?: ReactComponentLike,
  importColumnDefs?: ColumnDefinitions,
  columnDefs: ColumnDefinitions,
  comparatorConfig: ComparatorConfig,

  templateFileName?: string | null,
  templatePath?: string,
  generateTemplate?: VoidParametrisedFunction,
  disableGeneratingTemplate?: boolean,
  disableImporting?: boolean,

  isFetchingEntities: boolean,
  entities: Record<string, any>[],
  uploadedEntities: Record<string, any>[],
  uploadingEntities: boolean,

  selectFileToUpload?: VoidParametrisedFunction,
  importEntities?: VoidParametrisedFunction,

  additionalData?: any,

  initialFilters?: Record<string, any>,
  importProgressBarComponent?: ReactNode,
};

const AdminPageTabs = ({
  pageTitle,
  activeTab,
  canModify = false,
  changeTab,
  adminColumnRenderer,
  importColumnDefs,
  columnDefs,
  comparatorConfig: {
    idField,
    idFieldComparator,
    ignoredFields,
    entitiesValuesComparatorFactory,
  },

  templateFileName = null,
  templatePath,
  generateTemplate,
  disableGeneratingTemplate,
  disableImporting,

  isFetchingEntities,
  entities,
  uploadedEntities,
  uploadingEntities,

  selectFileToUpload,
  importEntities,
  additionalData,
  initialFilters,

  importProgressBarComponent,
}: AdminPageTabProps) => {
  useDefaultTab({
    canModify,
    changeTab,
    activeTab,
  });

  const changeTabHandler = useCallback((event, activeTab) => changeTab(activeTab), [changeTab]);

  if (canModify) {
    return (
      <Tabs
        defaultValue={TabTypes.VIEW}
        value={activeTab}
        onChange={changeTabHandler}
        className="a-tab-bar-margin-10"
        size="md"
      >
        <Tab value={TabTypes.VIEW} key={TabTypes.VIEW} label="View">
          <ViewGrid
            adminColumnRenderer={adminColumnRenderer}
            adminFieldName={idField}
            className="admin-import-grid"
            columnDefs={columnDefs}
            data={entities}
            isLoading={isFetchingEntities}
            shouldDisplayAdminColumn={canModify}
            initialFilters={initialFilters}
          />
        </Tab>
        <Tab value={TabTypes.IMPORT} key={TabTypes.IMPORT} label="Import">
          <AdminEntityImport
            entities={entities}
            uploadedEntities={uploadedEntities}
            uploadingEntities={uploadingEntities}
            selectFileToUpload={selectFileToUpload}
            importEntities={importEntities}
            columnDefs={importColumnDefs || columnDefs}
            templateFileName={templateFileName}
            templatePath={templatePath}
            generateTemplate={generateTemplate}
            disableGeneratingTemplate={disableGeneratingTemplate}
            disableImporting={disableImporting}
            pageTitle={pageTitle}
            idField={idField}
            idFieldComparator={idFieldComparator}
            ignoredFields={ignoredFields}
            entitiesValuesComparator={
              entitiesValuesComparatorFactory
              && entitiesValuesComparatorFactory(additionalData)
            }
            importProgressBarComponent={importProgressBarComponent}
          />
        </Tab>
      </Tabs>
    );
  }
  return (
    <Tabs
      defaultValue={TabTypes.VIEW}
      value={activeTab}
      onChange={changeTabHandler}
      className="a-tab-bar-margin-10"
      size="md"
    >
      <Tab value={TabTypes.VIEW} label="View">
        <ViewGrid
          adminColumnRenderer={adminColumnRenderer}
          className="admin-import-grid"
          columnDefs={columnDefs}
          data={entities}
          isLoading={isFetchingEntities}
          shouldDisplayAdminColumn={canModify}
        />
      </Tab>
    </Tabs>
  );
};

export default AdminPageTabs;
